<template>
  <div class="radio-input-container">
    <FormLabel :label="label" :isRequired="isRequired && !readOnly" />
    <div class="radio-input-wrapper">
      <!-- readonlyモードではテキスト表示 -->
      <div v-if="readOnly" class="readonly-text">
        {{ displayValue }}
      </div>
      <!-- 通常モードではラジオボタン -->
      <div v-else class="radio-options">
        <label
          v-for="option in options"
          :key="option.value"
          class="radio-option"
          :class="{ disabled: disabled }"
        >
          <input
            type="radio"
            :value="stringifyValue(option.value)"
            :checked="isValueEqual(value, option.value)"
            :disabled="disabled"
            :name="radioGroupName"
            class="radio-input"
            @change="onChange"
          />
          <span class="radio-label">{{ option.label }}</span>
        </label>
      </div>
    </div>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    <InputBottomDivider v-if="readOnly" />
  </div>
</template>

<script>
import FormLabel from "./FormLabel.vue";
import InputBottomDivider from "./InputBottomDivider.vue";

export default {
  name: "RadioInput",
  components: {
    FormLabel,
    InputBottomDivider,
  },
  props: {
    /**
     * ラベルテキスト
     * @type {String}
     * @required
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * 選択値
     * @type {String|Number|Boolean}
     * @default ""
     */
    value: {
      type: [String, Number, Boolean],
      default: "",
    },
    /**
     * オプションの配列
     * @type {Array}
     * @required
     * @example [{ label: "オプション1", value: "1" }, { label: "オプション2", value: "2" }]
     */
    options: {
      type: Array,
      required: true,
    },
    /**
     * ラジオボタンのname属性（同じnameでグループ化）
     * @type {String}
     * @default ""
     */
    name: {
      type: String,
      default: "",
    },
    /**
     * 必須項目かどうか
     * @type {Boolean}
     * @default false
     */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * 読み取り専用かどうか
     * @type {Boolean}
     * @default false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーメッセージ
     * @type {String}
     * @default ""
     */
    errorMessage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      // コンポーネントインスタンスごとにユニークなIDを生成
      uniqueId: `radio-${Math.random()
        .toString(36)
        .substr(2, 9)}`,
    };
  },
  computed: {
    /**
     * ラジオボタングループのname属性
     * nameプロパティが指定されている場合はそれを使用し、
     * 指定されていない場合は自動生成されたユニークIDを使用
     */
    radioGroupName() {
      return this.name || this.uniqueId;
    },
    displayValue() {
      if (
        this.value === null ||
        this.value === undefined ||
        this.value === ""
      ) {
        return "-";
      }
      const selectedOption = this.options.find(
        option => option.value === this.value
      );
      return selectedOption ? selectedOption.label : this.value;
    },
  },
  methods: {
    /**
     * 値を文字列に変換（HTMLのinput要素のvalue属性は文字列のため）
     */
    stringifyValue(val) {
      if (typeof val === "boolean") {
        return val.toString();
      }
      return String(val);
    },
    /**
     * 2つの値が等しいか比較（型を考慮）
     */
    isValueEqual(val1, val2) {
      // Boolean型の場合は厳密等価演算子で比較
      if (typeof val1 === "boolean" || typeof val2 === "boolean") {
        return val1 === val2;
      }
      // その他の場合は通常の比較
      return val1 === val2;
    },
    /**
     * ラジオボタンの変更イベントハンドラ
     */
    onChange(e) {
      // 単一選択を確実にするため、選択された値のみを発火
      const selectedValue = e.target.value;

      // optionsの最初の要素の型を確認して、適切な型に変換
      const firstOption = this.options[0];
      if (!firstOption) {
        this.$emit("input", selectedValue);
        return;
      }

      const optionValueType = typeof firstOption.value;
      let typedValue;

      if (optionValueType === "boolean") {
        // Boolean型の場合は文字列をBooleanに変換
        typedValue = selectedValue === "true";
      } else if (optionValueType === "number") {
        // Number型の場合は数値に変換
        typedValue = Number(selectedValue);
      } else {
        // String型の場合はそのまま
        typedValue = selectedValue;
      }

      this.$emit("input", typedValue);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.radio-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .radio-input-wrapper {
    .readonly-text {
      padding: 0rem 0.75rem;
      font-family: $aisea-font-family;
      font-size: 16px;
      font-weight: 400;
      color: $aisea-dark-gray;
      line-height: 1.5;
      min-height: 26px;
    }

    .radio-options {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;
      padding: 0.6rem 0.75rem;

      .radio-option {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-family: $aisea-font-family;
        font-size: 14px;
        font-weight: 400;
        color: $aisea-dark-gray;

        &.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .radio-input {
          margin-right: 0.5rem;
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #2d9cdb;

          &:disabled {
            cursor: not-allowed;
          }
        }

        .radio-label {
          user-select: none;
        }
      }
    }
  }

  .error-message {
    margin-top: 0.25rem;
    padding-left: 0.25rem;
    font-family: $aisea-font-family;
    font-size: 12px;
    font-weight: 400;
    color: $aisea-red;
    line-height: 1.5;
  }
}
</style>
